import {
  ChirpAvroRpcDetails,
  RpcClientV2,
  ChirpAvroRpcInputs,
  createRpcClientV2,
  HubHttpClient,
  ResultUnion,
} from 'rpc-client-utils';
import {
  Attach,
  makeFunctionalNext,
  NoParams,
  Resolver,
} from '../../definition/resolver';
import { toError, toLoading, toSuccess } from '../../definition/resolverState';
import { RejectUndefinedData } from '../../../resources/types';
import { attachPromise } from '../promise/attachPromise';
import { RpcError } from './RpcError';
import {
  ExtractRpcError,
  IDPAuthRpcResolver,
  RpcData,
  RpcResolver,
} from './rpcEntityTypes';
import { ENDPOINT_CONFIG_ARGS, toRpcInputs } from './endpointConfig';
import { Metrics } from '../../../metrics/metrics';

type FromRPCOptions<TRPC extends ChirpAvroRpcDetails<any, any, any, false>> = {
  rpc: TRPC;
  rpcClient: RpcClientV2;
};

const rpcAttach =
  <TRPC extends ChirpAvroRpcDetails<any, any, any, false>>(
    rpc: TRPC,
    rpcClient: RpcClientV2,
    toInputs: (params: any) => unknown
  ): Attach<any, RpcData<TRPC>, any> =>
  (params, runtime) => {
    const {
      env: { logger },
    } = runtime;
    const next = makeFunctionalNext(runtime);
    let pendingRefresh = false;

    const guardedFetch = async () => {
      if (pendingRefresh) {
        logger?.debug('refresh already pending');
        return;
      }

      pendingRefresh = true;

      Metrics.counter('resolver-refresh', { transport: 'chirp' }).increment();

      logger?.debug('fetching');

      const callSiteError = new Error();
      next(toLoading());

      try {
        const result = await rpcClient.callWithResultUnion(
          rpc,
          toInputs(params) as ChirpAvroRpcInputs<TRPC>
        );

        if (result.type === 'data') {
          logger?.debug('success');
          next(toSuccess(result.data));
        } else {
          logger?.debug('rpc error');
          next(toError(new RpcError(result.rpcError, callSiteError.stack)));
        }
      } catch (error) {
        logger?.debug('error');
        next(toError(error as Error));
      }

      pendingRefresh = false;
    };

    return {
      refresh: () => void guardedFetch(),
      detach: () => {},
    };
  };

/**
 * Resolver for a CHIRP RPC. Params are the RPC's request type. For idp-auth
 * services that also accept env/hublet/impersonation params, use
 * `fromIDPAuthRPC`.
 */
export const fromRPC = <
  TRPC extends ChirpAvroRpcDetails<any, any, any, false>,
>({
  rpc,
  rpcClient,
}: FromRPCOptions<TRPC>): RpcResolver<TRPC> => ({
  name: rpc.rpcName,
  tags: ['CHIRP', 'promise'],
  attach: rpcAttach(rpc, rpcClient, (params) => ({ request: params })),
});

/**
 * Resolver for an idp-auth CHIRP RPC. Params are the request type plus the
 * endpoint configuration (env/hublet/impersonation), which are peeled off
 * before calling and folded into the resource's cache identity.
 */
export const fromIDPAuthRPC = <
  TRPC extends ChirpAvroRpcDetails<any, any, any, false>,
>({
  rpc,
  rpcClient,
}: FromRPCOptions<TRPC>): IDPAuthRpcResolver<TRPC> => ({
  name: rpc.rpcName,
  tags: ['CHIRP', 'promise'],
  args: ENDPOINT_CONFIG_ARGS as unknown as IDPAuthRpcResolver<TRPC>['args'],
  attach: rpcAttach(rpc, rpcClient, toRpcInputs),
});

export const fromRPCCall = <
  TRPC extends ChirpAvroRpcDetails<any, any, any, false>,
  TData,
  TParams = NoParams,
>({
  rpc,
  fetch,
}: {
  rpc: TRPC;
  fetch: (
    call: (inputs: ChirpAvroRpcInputs<TRPC>) => Promise<RpcData<TRPC>>,
    params: TParams
  ) => Promise<TData>;
}): RejectUndefinedData<
  TData,
  Resolver<TParams, TData, Error | RpcError<ExtractRpcError<ResultUnion<TRPC>>>>
> =>
  ({
    name: rpc.rpcName,
    tags: ['CHIRP', 'promise'],
    attach: (params, runtime) => {
      const rpcClient = createRpcClientV2({
        hubHttpClient: runtime.env.httpClient as HubHttpClient,
      });

      const call = async (inputs: ChirpAvroRpcInputs<TRPC>) => {
        const callSiteError = new Error();
        const result = await rpcClient.callWithResultUnion(rpc, inputs);

        if (result.type === 'rpcError') {
          throw new RpcError(result.rpcError, callSiteError.stack);
        }

        return result.data;
      };

      return attachPromise(() => fetch(call, params), params, runtime, 'chirp');
    },
  }) as RejectUndefinedData<
    TData,
    Resolver<
      TParams,
      TData,
      Error | RpcError<ExtractRpcError<ResultUnion<TRPC>>>
    >
  >;
